// src/pages/customer/checkout/StripeSuccessPage.jsx
// Page to handle return from Stripe hosted checkout

import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { processStripeCheckoutSuccess } from "../../../firebase/stripe-checkout-functions";
import { clearCart } from "../../../firebase/cart-functions";
import { toast } from "react-toastify";
import "./StripeSuccessPage.css";

const StripeSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    handleStripeSuccess();
  }, [handleStripeSuccess]);

  const handleStripeSuccess = useCallback(async () => {
    try {
      // Get session ID from URL params
      const sessionId = searchParams.get("session_id");

      if (!sessionId) {
        throw new Error("No session ID found in URL");
      }

      // Process the successful payment
      const result = await processStripeCheckoutSuccess(sessionId);

      if (result && result.success) {
        // setOrderData(result.orderData); // orderData state removed as unused

        // Clear the cart
        await clearCart();

        // Clear pending order from localStorage
        localStorage.removeItem("pendingOrder");

        // Store order info for confirmation page
        localStorage.setItem(
          "lastOrderInfo",
          JSON.stringify({
            orderId: result.orderId,
            storeId: result.orderData?.storeId,
            timestamp: new Date().toISOString(),
            paymentMethod: "credit_card",
          }),
        );

        // Store full order data for guest users (to avoid permission issues)
        if (result.orderData) {
          localStorage.setItem(
            "lastOrderData",
            JSON.stringify({
              orderData: result.orderData,
              timestamp: new Date().toISOString(),
              expiresAt: new Date(Date.now() + 10 * 60 * 1000).toISOString(), // 10 minutes
            }),
          );
        }

        // Show success message
        toast.success("Payment successful! Your order has been placed.");

        // Redirect to order confirmation page
        setTimeout(() => {
          navigate(`/order-confirmation/${result.orderId}`);
        }, 2000);
      } else {
        throw new Error("Failed to process order");
      }
    } catch (error) {
      console.error("Error processing Stripe success:", error);
      setError(
        error.message ||
          "Failed to process your payment. Please contact support.",
      );

      // Show error message
      toast.error(error.message || "Payment processing failed");
    } finally {
      setProcessing(false);
    }
  }, [searchParams, navigate]); // useCallback dependencies

  if (processing) {
    return (
      <div className="stripe-success-page">
        <div className="stripe-success-container">
          <div className="stripe-success-content">
            <div className="loading-spinner"></div>
            <h2>Processing Your Payment...</h2>
            <p>Please wait while we confirm your order.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="stripe-success-page">
        <div className="stripe-success-container">
          <div className="stripe-success-content error">
            <div className="error-icon">⚠️</div>
            <h2>Payment Processing Error</h2>
            <p>{error}</p>
            <div className="error-actions">
              <button
                onClick={() => navigate("/checkout")}
                className="retry-btn"
              >
                Return to Checkout
              </button>
              <button
                onClick={() => navigate("/contact")}
                className="contact-btn"
              >
                Contact Support
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="stripe-success-page">
      <div className="stripe-success-container">
        <div className="stripe-success-content success">
          <div className="success-icon">✅</div>
          <h2>Payment Successful!</h2>
          <p>Your order has been placed and confirmed.</p>
          <p>Redirecting to order confirmation...</p>
        </div>
      </div>
    </div>
  );
};

export default StripeSuccessPage;
